import {
  CellState,
  CellType,
  ClueType,
  FeedFormat,
  ICell,
  IClue,
  IFeedMetaData,
  IPuzzleData,
  PuzzleType,
} from "../types";

import { unpackData } from "js/crossword/converters/utils/unpackData"

export default function convertCN2I(rawData: IRawData): IPuzzleData {
  const { data, metaData } = rawData; 

  const unpackedData: ICrosswordData = unpackData(data.crossword);
  const { rows, columns, rawClues, rawCells, rawWords } = filterData(unpackedData);

  const cells = rawCells.map(createCell);
  const clues = remapRawClues(rawClues)
    .map((data, index) => createClue(
      index,
      data,
      cells,
      rawWords,
      { rows, columns },
    ))
    .sort((a, b) => {
      return a.number - b.number;
    });

  return {
    rows,
    columns,
    cells,
    clues,
    id: metaData.id,
    type: PuzzleType.Regular,
    metaData: {
      date: metaData.date,
      title: "Crossword",
      author: metaData.author,
      format: FeedFormat.CN2I_HANNEQUART,    
    },
  }
}

function filterData(data: ICrosswordData): {
  rows: number;
  columns: number;
  rawCells: IDataCell[];
  rawClues: IDataClueGroup[];
  rawWords: IDataWord[];
} {
  const { grid, clues, word } = data;
  const rawCells = Array.from(grid.cell);

  rawCells.sort((a, b) => {
    return Number(a.y) - Number(b.y);
  });

  return {
    rawCells,
    rawClues: clues,
    rawWords: word,
    rows: Number(grid.height),
    columns: Number(grid.width),
  };
}

function remapRawClues(groups: IDataClueGroup[]): IRemappedClue[] {
  const remappedClues: IRemappedClue[] = [];
  
  groups.forEach(group => {
    group.clue.forEach(clue => {
      remappedClues.push({
        number: Number(clue.number),
        text: clue.text,
        type: group.title.b === ItemType.ACROSS ? ClueType.Across : ClueType.Down,
        wordId: clue.word,
      });
    });
  });

  return remappedClues;
}

function createClue(
  index: number,
  clueData: IRemappedClue,
  cells: ICell[],
  rawWords: IDataWord[],
  size: IGridSize,
): IClue {
  const { text, type } = clueData;
  const word = rawWords.find(w => w.id === clueData.wordId);
  const cellIds = getClueCells(word!, size);
  const firstCellIndex = Number(cellIds[0]) - 1;

  return {
    text,
    type,
    cells: cellIds,
    id: `${index}`,
    number: Number(cells[firstCellIndex].number),
  };
}

function getClueCells(word: IDataWord, size: IGridSize): string[] {
  const horCoord = word.x.split("-").map(Number);
  const vertCoord = word.y.split("-").map(Number);

  if (horCoord.length === 2) {
    const row = vertCoord[0]; 
    return getCellNumber(horCoord[0], horCoord[1])
      .map(n => ((row - 1) * size.columns) + n)
      .map(String);
  }

  const column = horCoord[0]; 
  return getCellNumber(vertCoord[0], vertCoord[1])
    .map(n => ((n - 1) * size.columns) + column)
    .map(String);
}

function getCellNumber(first: number, last: number): number[] {
  return new Array((last + 1) - first).fill(0).map((_, i) => first + i);
}

function createCell(rawCell: IDataCell, index: number): ICell {
  return {
    id: `${index + 1}`,
    x: Number(rawCell.x) - 1,
    y: Number(rawCell.y) - 1,
    type: rawCell.type ? CellType.Empty : CellType.Regular,
    number: Number(rawCell.number),
    solution: rawCell.solution,
    letter: "",
    state: CellState.Normal,
    circle: false,
  };
}

// types


enum ItemType {
  ACROSS = "ACROSS",
  DOWN = "DOWN",
}

interface IGridSize {
  rows: number;
  columns: number;
}

interface IRawData {
  metaData: IFeedMetaData;
  data: {
    crossword: unknown;
  };
}

interface ICrosswordData {
  clues: IDataClueGroup[];
  grid: {
    width: string;
    height: string;
    cell: IDataCell[];
  };
  word: IDataWord[];
}

interface IDataClueGroup {
  clue: IDataClue[];
  title: {
    b: ItemType;
  };
}

interface IDataClue {
  text: string;
  format: string;
  number: string;
  word: string;
}

interface IRemappedClue {
  number: number;
  text: string;
  type: ClueType;
  wordId: string;
}

interface IDataCell {
  solution: string;
  x: string;
  y: string;
  number?: string;
  type?: "block";
}

interface IDataWord {
  id: string;
  x: string;
  y: string;
}
